package dev.akshaychordiya.components.widgets

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import dev.akshaychordiya.toSitePalette
import org.jetbrains.compose.web.css.AlignSelf
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img

/**
 * Shape of a preview image.
 *
 * @param ratio CSS `aspect-ratio` value. Video frames and article leads are 16:9; book covers are portrait.
 * @param widthRem Rendered width once there's room for a side-by-side layout.
 */
enum class PreviewShape(val ratio: String, val widthRem: Double) {
    WIDE("16 / 9", 10.0),
    COVER("4 / 5", 6.5),
}

/**
 * The frame.
 *
 * The ratio lives on this container rather than on the `<img>` on purpose. On a replaced element like `<img>`,
 * `aspect-ratio` competes with the file's intrinsic dimensions and loses often enough to be unusable - a 16:9 rule
 * was rendering 1024x1044 screenshots at their own ratio instead. A plain `<div>` honours it reliably, and clipping
 * the image inside with `object-fit: cover` means every card in a list ends up exactly the same height.
 */
val PreviewFrameStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .borderRadius(0.5.cssRem)
            .overflow(Overflow.Hidden)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            // A neutral fill means a slow image leaves a calm placeholder rather than a white flash.
            .backgroundColor(colorMode.toSitePalette().surface)
    }
}

// As a row flex item the frame would otherwise inherit `align-items: stretch` and be pulled to the height of the
// text beside it, which silently overrides the aspect ratio. `align-self: flex-start` is what makes the ratio stick.
private fun Modifier.asSideThumbnail(widthRem: Double) = this
    .width(widthRem.cssRem)
    .flexShrink(0)
    .alignSelf(AlignSelf.FlexStart)

val PreviewWideStyle = CssStyle {
    base { Modifier.styleModifier { property("aspect-ratio", PreviewShape.WIDE.ratio) } }
    // Full width on a phone so the image is worth looking at; a fixed side thumbnail from tablet up.
    Breakpoint.SM { Modifier.asSideThumbnail(PreviewShape.WIDE.widthRem) }
}

val PreviewCoverStyle = CssStyle {
    base { Modifier.styleModifier { property("aspect-ratio", PreviewShape.COVER.ratio) } }
    Breakpoint.SM { Modifier.asSideThumbnail(PreviewShape.COVER.widthRem) }
}

val PreviewImageStyle = CssStyle.base {
    Modifier.fillMaxSize().objectFit(ObjectFit.Cover).display(DisplayStyle.Block)
}

/**
 * A preview thumbnail.
 *
 * Loaded lazily and decoded off the main thread, so a long list neither blocks first render nor janks while the
 * images arrive.
 */
@Composable
fun Preview(src: String, alt: String, shape: PreviewShape = PreviewShape.WIDE) {
    val shapeStyle = if (shape == PreviewShape.COVER) PreviewCoverStyle else PreviewWideStyle

    Div(PreviewFrameStyle.toModifier().then(shapeStyle.toModifier()).toAttrs()) {
        Img(
            src = src,
            alt = alt,
            attrs = PreviewImageStyle.toModifier().toAttrs {
                attr("loading", "lazy")
                attr("decoding", "async")
            },
        )
    }
}
